import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getDashboardAccess,
  getWarehouseAccess,
  getSelectedWarehouseId,
  setAuthSession,
} from "./components/authStore";
import { AuthService } from "../../services/auth.service";
import { warehouseService } from "../../services/warehouse.service";
import AuthPopup from "./components/AuthPopup";

export default function SelectDashboard() {
  const nav = useNavigate();
  const dashboardAccess = getDashboardAccess();
  const warehouseAccess = getWarehouseAccess();

  const canOpenAdmin = !!dashboardAccess?.canOpenAdminDashboard;
  const canOpenWarehouse = !!dashboardAccess?.canOpenWarehouseDashboard;

  const assignedWarehouseId = warehouseAccess?.assignedWarehouseId || "";
  const accessibleWarehouseIds = Array.isArray(warehouseAccess?.warehouseIds)
    ? warehouseAccess.warehouseIds
    : [];

  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(
    getSelectedWarehouseId() || assignedWarehouseId || ""
  );
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [popup, setPopup] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onClose: null,
  });

  const showPopup = ({ type = "error", title = "Alert", message, onClose = null }) => {
    setPopup({ open: true, type, title, message, onClose });
  };

  const closePopup = () => {
    const cb = popup.onClose;
    setPopup((p) => ({ ...p, open: false, onClose: null }));
    cb?.();
  };

  useEffect(() => {
    let ignore = false;

    async function loadWarehouses() {
      if (!canOpenWarehouse) return;

      try {
        setLoadingWarehouses(true);

        const res = await warehouseService.listWarehouses({
          page: 1,
          limit: 200,
        });

        const items = Array.isArray(res?.items) ? res.items : [];

        let filtered = items;

        if (warehouseAccess?.mode === "selected" && accessibleWarehouseIds.length > 0) {
          filtered = items.filter((w) => accessibleWarehouseIds.includes(w.id));
        }

        if (warehouseAccess?.mode === "assigned" && assignedWarehouseId) {
          filtered = items.filter((w) => w.id === assignedWarehouseId);
        }

        if (!ignore) {
          setWarehouseOptions(filtered);

          const firstId = filtered?.[0]?.id || "";
          setSelectedWarehouseId((prev) => prev || assignedWarehouseId || firstId || "");
        }
      } catch (err) {
        console.error("Failed to load warehouses", err);
      } finally {
        if (!ignore) setLoadingWarehouses(false);
      }
    }

    loadWarehouses();

    return () => {
      ignore = true;
    };
  }, [canOpenWarehouse, warehouseAccess?.mode, assignedWarehouseId, accessibleWarehouseIds.join(",")]);

  const warehouseButtonDisabled = useMemo(() => {
    if (!canOpenWarehouse) return true;
    if (loadingWarehouses) return true;

    const mode = warehouseAccess?.mode;

    if (mode === "assigned") return false;
    if (mode === "selected" || mode === "all") return !selectedWarehouseId;

    return true;
  }, [canOpenWarehouse, loadingWarehouses, warehouseAccess?.mode, selectedWarehouseId]);

  const openAdmin = async () => {
    try {
      if (!canOpenAdmin || switching) return;

      setSwitching(true);

      const res = await AuthService.switchContext({
        dashboard: "admin",
      });

      setAuthSession({
        accessToken: res?.accessToken,
        admin: res?.admin,
        dashboardAccess: res?.dashboardAccess,
        warehouseAccess: res?.warehouseAccess,
        currentDashboard: res?.currentDashboard,
        selectedWarehouseId: res?.selectedWarehouseId,
      });

      nav("/admin/dashboard", { replace: true });
    } catch (err) {
      showPopup({
        type: "error",
        title: "Failed to Open Admin Dashboard",
        message: err?.message || "Failed to open admin dashboard",
      });
      
    } finally {
      setSwitching(false);
    }
  };

  const openWarehouse = async () => {
    try {
      if (!canOpenWarehouse || switching) return;

      const finalWarehouseId =
        assignedWarehouseId || selectedWarehouseId || warehouseOptions?.[0]?.id || null;

      if (!finalWarehouseId) {
        showPopup({
          type: "error",
          title: "Select Warehouse",
          message: "Please select warehouse first.",
        });
        return;
      }

      setSwitching(true);

      const res = await AuthService.switchContext({
        dashboard: "warehouse",
        warehouseId: finalWarehouseId,
      });

      setAuthSession({
        accessToken: res?.accessToken,
        admin: res?.admin,
        dashboardAccess: res?.dashboardAccess,
        warehouseAccess: res?.warehouseAccess,
        currentDashboard: res?.currentDashboard,
        selectedWarehouseId: res?.selectedWarehouseId,
      });

      nav("/admin/dashboard", { replace: true });
    } catch (err) {
      showPopup({
        type: "error",
        title: "Failed to open warehouse dashboard",
        message: err?.message || "Failed to open warehouse dashboard",
      });

    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f6f6fb] px-4">
      <div className="w-full max-w-[760px] rounded-[24px] border border-[#f2c6cc] bg-white p-8 shadow-sm">
        <h2 className="text-center text-[28px] font-bold text-slate-800">
          Select Dashboard
        </h2>

        <p className="mt-2 text-center text-[14px] text-slate-500">
          Choose which panel you want to open
        </p>

        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          <button
            type="button"
            onClick={openAdmin}
            disabled={!canOpenAdmin || switching}
            className={`rounded-[18px] border p-6 text-left transition ${canOpenAdmin
              ? "border-[#f2c6cc] hover:bg-[#fff7f8]"
              : "cursor-not-allowed border-slate-200 opacity-50"
              }`}
          >
            <div className="text-[20px] font-bold text-slate-800">Admin Panel</div>
            <div className="mt-2 text-[13px] text-slate-500">
              Manage roles, employees, products, inventory and more
            </div>
          </button>

          <div
            className={`rounded-[18px] border p-6 transition ${canOpenWarehouse
              ? "border-[#f2c6cc]"
              : "cursor-not-allowed border-slate-200 opacity-50"
              }`}
          >
            <div className="text-[20px] font-bold text-slate-800">Warehouse Panel</div>
            <div className="mt-2 text-[13px] text-slate-500">
              Manage warehouse inventory, stock and warehouse-side operations
            </div>

            {canOpenWarehouse && (
              <div className="mt-5">
                <label className="mb-2 block text-[12px] font-semibold text-slate-600">
                  Select Warehouse
                </label>

                <select
                  value={assignedWarehouseId || selectedWarehouseId}
                  onChange={(e) => setSelectedWarehouseId(e.target.value)}
                  disabled={
                    switching ||
                    loadingWarehouses ||
                    warehouseAccess?.mode === "assigned"
                  }
                  className="h-[44px] w-full rounded-[12px] border border-[#f2c6cc] bg-white px-4 text-[13px] text-slate-700 outline-none focus:border-[#d86a78]"
                >
                  <option value="">
                    {loadingWarehouses ? "Loading warehouses..." : "Select warehouse"}
                  </option>

                  {warehouseOptions.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name || w.warehouseName || w.id}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              type="button"
              onClick={openWarehouse}
              disabled={warehouseButtonDisabled || switching}
              className="mt-5 h-[44px] w-full rounded-[12px] bg-[#d86a78] text-[14px] font-semibold text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {switching ? "Opening..." : "Open Warehouse Panel"}
            </button>
          </div>
        </div>
      </div>
      <AuthPopup
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
    </div>
  );
}